import { useLatest } from 'ahooks';
import classnames from 'classnames';
import React, { useEffect } from 'react';
import { Button, Modal } from 'tea-component';
import { useIntl } from 'umi';

import type { IRecord } from '@gp/types/global';
import type { IBaseProps } from '@home/types/common';

import './index.scss';

type TBtnLabel = 'Confirm' | 'Cancel' | 'Close' | any;
type TBtnKey = 'confirm' | 'cancel' | 'close' | any;

interface IBtnDefaultGroupItem {
  type: 'primary' | 'weak';
  disabled?: boolean;
  loading?: boolean;
  ClickFn?: Function | null;
  text?: any;
}

interface IGeneralModalProps extends IBaseProps {
  title?: string;
  visible: boolean;
  modalSize?: string | number;
  content: any;
  isFooter?: boolean;
  footerButtonGroup?: TBtnKey[];
  confirmButtonText?: TBtnLabel;
  cancelButtonText?: TBtnLabel;
  closeButtonText?: TBtnLabel;
  destroyOnClose?: boolean;
  disabledConfirm?: boolean;
  loadingConfirm?: boolean;
  onConfirm?: Function | null;
  onOpen?: Function | null;
  onClose?: Function | null;
}

const LOCALE_MAP: IRecord<string> = {
  Confirm: 'dbg_confirm',
  Cancel: 'dbg_cancel',
  Close: 'dbg_close',
};

const GeneralModal: React.FC<IGeneralModalProps> = (props) => {
  const {
    style,
    className,
    title = '',
    visible = false,
    modalSize = 'auto',
    content = '',
    isFooter = true,
    footerButtonGroup = ['confirm', 'cancel'],
    // 自定义按钮
    customFooterButtonGroup,
    confirmButtonText = 'Confirm',
    cancelButtonText = 'Cancel',
    closeButtonText = 'Close',
    destroyOnClose = true,
    disabledConfirm = false,
    loadingConfirm = false,
    onConfirm,
    onOpen,
    onClose,
  } = props;

  const { formatMessage } = useIntl();

  const onOpenLatest = useLatest(onOpen);

  // 按钮默认配置
  const btnDefaultMap: IRecord<IBtnDefaultGroupItem> = {
    confirm: {
      type: 'primary',
      disabled: disabledConfirm,
      loading: loadingConfirm,
      ClickFn: onConfirm,
      text: confirmButtonText,
    },
    cancel: {
      type: 'weak',
      ClickFn: onClose,
      text: cancelButtonText,
    },
    close: {
      type: 'weak',
      ClickFn: onClose,
      text: closeButtonText,
    },
  };

  // eslint-disable-next-line arrow-body-style
  const getFooter = (footerGroup: TBtnKey[]) => {
    return footerGroup.map((key: TBtnKey) => {
      // 如果是默认按钮，则使用默认的配置
      if (Reflect.has(btnDefaultMap, key)) {
        const matchConfig = btnDefaultMap[key];
        const { type, disabled = false, loading = false, ClickFn, text } = matchConfig || {};
        const localeText = LOCALE_MAP?.[text];
        return (
          <Button
            key={key}
            type={type}
            disabled={disabled}
            loading={loading}
            onClick={() => ClickFn?.()}
          >
            {typeof text === 'string' && !!localeText ? formatMessage({ id: localeText }) : text}
          </Button>
        );
      }
      if (!customFooterButtonGroup) return null;
      // 如果是自定义按钮，则使用customFooterButtonGroup的配置
      const { type, text, ClickFn } = customFooterButtonGroup[key] || {};
      return (
        text && (
          <Button type={type} onClick={() => ClickFn?.()}>
            {text}
          </Button>
        )
      );
    });
  };

  useEffect(() => {
    Modal.onOpen(() => {
      onOpenLatest.current?.();
    });
  }, []);

  return (
    <Modal
      style={style}
      className={classnames('dbg-general-modal', className)}
      visible={visible}
      size={modalSize}
      caption={title}
      destroyOnClose={destroyOnClose}
      onClose={() => onClose?.()}
    >
      <Modal.Body>{content}</Modal.Body>
      {isFooter && <Modal.Footer>{footerButtonGroup && getFooter(footerButtonGroup)}</Modal.Footer>}
    </Modal>
  );
};

export default React.memo(GeneralModal);
